/*
 * ============================================================================
 * Céragrès trade calculator
 * ----------------------------------------------------------------------------
 * Wall dimensions in → block count + freight estimate out. Live, no submit.
 *
 * ⚙️  ALL product, freight, and business data lives in the CONFIG object
 *     directly below. Update values here — do not touch the logic beneath it.
 *
 * MSRP ONLY. This file must never contain Céragrès cost, dealer pricing,
 * or margin: the page is reachable by URL without authentication.
 * ============================================================================
 */

const CERAGRES_CONFIG = {
  products: {
    large: {
      label: 'Large — 290mm',
      blockMm: 290, //     nominal face dimension; module = block + mortar joint
      unitsPerPallet: 104, //  see note in placeholders — pallet vs. case divisibility
      unitsPerCase: 6,
      msrp: { white: 30.00, grey: 26.67 }, // CAD per block, retail
    },
    small: {
      label: 'Small — 190mm',
      blockMm: 190, //     nominal face dimension; module = block + mortar joint
      unitsPerPallet: 400,
      unitsPerCase: 12,
      msrp: { white: 13.33, grey: 12.50 }, // CAD per block, retail
    },
  },

  // Mortar joint options (mm). The joint adds to the block face to give the
  // module dimension used for coverage. Keep these in sync with the <select>
  // options in sections/ceragres-trade.liquid.
  joints: {
    6: '6mm (1/4") — tight',
    10: '10mm (3/8") — standard',
    13: '13mm (1/2") — wide',
  },
  defaultJointMm: 10,

  freightPerPallet: 825, //  CAD, Kamloops → 825 Deslauriers, Montreal
  minFreight: 125, //        CONFIRM before launch (assumed)
  overage: 0.10, //          10% cut/waste allowance
  sqftPerSqm: 10.7639,

  // Wall area (sq ft) above which we stop quoting and route to a volume quote.
  largeProjectThreshold: 500,
};

/* ----------------------------------------------------------------------------
 * Logic — no business data below this line.
 * -------------------------------------------------------------------------- */

(function () {
  'use strict';

  const DASH = '—';
  const S = CERAGRES_CONFIG.sqftPerSqm; // sq ft per sq m
  const M_PER_FT = 0.3048; // exact

  // Per-language display strings. The active language comes from the
  // data-locale attribute the section stamps on the calculator root, which
  // tracks the site-wide language selector. Currency stays CAD in both.
  const L10N = {
    en: { intl: 'en-CA', sqft: 'sq ft' },
    fr: { intl: 'fr-CA', sqft: 'pi²' },
  };

  // Build the formatters and display systems for one language. Calculations run
  // in imperial (sq ft) internally; metric converts at the display edge only.
  // `area`/`coverage` take sq ft, `blocksPer` takes coverage (sq ft/block),
  // `freightPer` takes $/sq ft.
  function makeFormatter(lang) {
    const t = L10N[lang] || L10N.en;

    const CAD = new Intl.NumberFormat(t.intl, {
      style: 'currency',
      currency: 'CAD',
      minimumFractionDigits: 2,
      maximumFractionDigits: 2,
    });
    const INT = new Intl.NumberFormat(t.intl, { maximumFractionDigits: 0 });
    // Fixed-decimal helper — French uses a comma decimal mark, so never toFixed().
    const dec = (value, digits) =>
      new Intl.NumberFormat(t.intl, {
        minimumFractionDigits: digits,
        maximumFractionDigits: digits,
      }).format(value);

    return {
      CAD: CAD,
      INT: INT,
      systems: {
        imperial: {
          areaLabel: t.sqft,
          area: (sqft) => INT.format(Math.round(sqft)) + ' ' + t.sqft,
          coverage: (sqft) => dec(sqft, 2) + ' ' + t.sqft,
          blocksPer: (coverage) => dec(1 / coverage, 2),
          freightPer: (perSqft) => CAD.format(perSqft),
        },
        metric: {
          areaLabel: 'm²',
          area: (sqft) => dec(sqft / S, 1) + ' m²',
          coverage: (sqft) => dec(sqft / S, 3) + ' m²',
          blocksPer: (coverage) => dec(S / coverage, 1),
          freightPer: (perSqft) => CAD.format(perSqft * S),
        },
      },
    };
  }

  function sqftPerBlock(product, jointMm, cfg) {
    const moduleM = (product.blockMm + jointMm) / 1000; // block face + mortar joint
    return moduleM * moduleM * cfg.sqftPerSqm;
  }

  function compute(state, cfg) {
    const product = cfg.products[state.profile];
    const coverage = sqftPerBlock(product, state.jointMm, cfg); // sq ft covered by one block
    const wallArea = state.wallArea; // sq ft (readState converts metric input)

    // Blank / zero / invalid → return a shell the view renders as dashes.
    if (!(wallArea > 0)) {
      return { empty: true, units: state.units, coverage, blocksPerSqft: 1 / coverage };
    }

    const blocksRaw = Math.ceil((wallArea / coverage) * (1 + cfg.overage));
    const cases = Math.ceil(blocksRaw / product.unitsPerCase);
    const blocks = cases * product.unitsPerCase; // always sell in full cases

    const palletCapacitySqft = product.unitsPerPallet * coverage;
    const freightPerSqft = cfg.freightPerPallet / palletCapacitySqft;

    const uncappedFreight = wallArea * freightPerSqft;
    const freight = Math.max(cfg.minFreight, uncappedFreight);
    const minFreightApplied = uncappedFreight < cfg.minFreight;

    const unitPrice = product.msrp[state.colour];
    const productTotal = blocks * unitPrice;
    const total = productTotal + freight;

    return {
      empty: false,
      units: state.units,
      wallArea,
      coverage,
      blocksPerSqft: 1 / coverage,
      blocksRaw,
      cases,
      blocks,
      freightPerSqft,
      freight,
      minFreightApplied,
      productTotal,
      total,
      overThreshold: wallArea > cfg.largeProjectThreshold,
    };
  }

  function render(root, r, fmt) {
    const set = (key, value) => {
      const el = root.querySelector('[data-out="' + key + '"]');
      if (el) el.textContent = value;
    };
    const toggle = (key, on) => {
      const el = root.querySelector('[data-note="' + key + '"]');
      if (el) el.hidden = !on;
    };

    const u = fmt.systems[r.units] || fmt.systems.imperial;

    // Swap the per-area unit in labels ("Blocks per sq ft" ↔ "Blocks per m²").
    root.querySelectorAll('[data-unit-label]').forEach((el) => {
      el.textContent = u.areaLabel;
    });

    if (r.empty) {
      set('wallArea', DASH);
      set('blocks', DASH);
      set('cases', DASH);
      set('productTotal', DASH);
      set('freight', DASH);
      set('coverage', u.coverage(r.coverage));
      set('blocksPerSqft', u.blocksPer(r.coverage));
      set('freightPerSqft', DASH);
      set('total', DASH);
      toggle('minFreight', false);
      toggle('largeProject', false);
      return;
    }

    set('wallArea', u.area(r.wallArea));
    set('blocks', fmt.INT.format(r.blocks));
    set('cases', fmt.INT.format(r.cases));
    set('productTotal', fmt.CAD.format(r.productTotal));
    set('freight', fmt.CAD.format(r.freight));
    set('coverage', u.coverage(r.coverage));
    set('blocksPerSqft', u.blocksPer(r.coverage));
    set('freightPerSqft', u.freightPer(r.freightPerSqft));
    set('total', fmt.CAD.format(r.total));
    toggle('minFreight', r.minFreightApplied);
    toggle('largeProject', r.overThreshold);
  }

  // Non-negative number from a named input, or 0 for blank/invalid.
  function inputNum(root, name) {
    const el = root.querySelector('[data-in="' + name + '"]');
    const raw = el ? parseFloat(el.value) : NaN;
    return Number.isFinite(raw) && raw >= 0 ? raw : 0;
  }

  function currentUnits(root) {
    const el = root.querySelector('[data-in="units"]:checked');
    return el && el.value === 'metric' ? 'metric' : 'imperial';
  }

  function readState(root) {
    const val = (name) => {
      const el = root.querySelector('[data-in="' + name + '"]');
      return el ? el.value : '';
    };

    const jointRaw = parseInt(val('joint'), 10);
    const jointMm = Object.prototype.hasOwnProperty.call(CERAGRES_CONFIG.joints, jointRaw)
      ? jointRaw
      : CERAGRES_CONFIG.defaultJointMm;

    const units = currentUnits(root);

    // Compute wall area in sq ft internally, from whichever inputs are active.
    let wallArea;
    if (units === 'metric') {
      const widthM = inputNum(root, 'widthM');
      const heightM = inputNum(root, 'heightM');
      wallArea = widthM * heightM * S; // m² → sq ft
    } else {
      const widthFt = inputNum(root, 'widthFt') + inputNum(root, 'widthIn') / 12;
      const heightFt = inputNum(root, 'heightFt') + inputNum(root, 'heightIn') / 12;
      wallArea = widthFt * heightFt;
    }

    return {
      profile: val('profile') === 'small' ? 'small' : 'large',
      colour: val('colour') === 'grey' ? 'grey' : 'white',
      jointMm: jointMm,
      units: units,
      wallArea: wallArea,
    };
  }

  // Show the dimension inputs for the active system, hide the other.
  function setMode(root, units) {
    root.querySelectorAll('[data-dim="imperial"]').forEach((el) => {
      el.hidden = units !== 'imperial';
    });
    root.querySelectorAll('[data-dim="metric"]').forEach((el) => {
      el.hidden = units !== 'metric';
    });
  }

  // Carry the entered wall size across a system switch so it stays constant.
  function convertDims(root, from, to) {
    const setVal = (name, value) => {
      const el = root.querySelector('[data-in="' + name + '"]');
      if (el) el.value = value;
    };

    ['width', 'height'].forEach((dim) => {
      if (from === 'imperial' && to === 'metric') {
        const ft = inputNum(root, dim + 'Ft') + inputNum(root, dim + 'In') / 12;
        const m = ft * M_PER_FT;
        setVal(dim + 'M', m ? m.toFixed(2) : '');
      } else if (from === 'metric' && to === 'imperial') {
        const totalFt = inputNum(root, dim + 'M') / M_PER_FT;
        let ft = Math.floor(totalFt);
        let inch = Math.round((totalFt - ft) * 12);
        if (inch === 12) { ft += 1; inch = 0; }
        setVal(dim + 'Ft', String(ft));
        setVal(dim + 'In', String(inch));
      }
    });
  }

  function init(root) {
    const fmt = makeFormatter((root.getAttribute('data-locale') || 'en').slice(0, 2));
    let lastUnits = currentUnits(root);
    setMode(root, lastUnits);

    const update = () => {
      const units = currentUnits(root);
      if (units !== lastUnits) {
        convertDims(root, lastUnits, units);
        setMode(root, units);
        lastUnits = units;
      }
      render(root, compute(readState(root), CERAGRES_CONFIG), fmt);
    };

    root.addEventListener('input', update);
    root.addEventListener('change', update);
    update(); // paint defaults on load
  }

  function boot() {
    document.querySelectorAll('[data-ceragres-calculator]').forEach(init);
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', boot);
  } else {
    boot();
  }
})();
